import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPlus,
  faTrash,
  faSpinner,
  faStethoscope,
  faBed,
  faSyringe,
  faUserDoctor,
  faFileMedical,
  faExpand,
  faCompress,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "36px",
    fontSize: "0.875rem",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const RNB_OPTIONS = [
  { label: "Ward", value: "Ward", rm_amt: 120 },
  { label: "Semi-Private", value: "Semi-Private", rm_amt: 125 },
  { label: "Private", value: "Private", rm_amt: 130 },
  { label: "Small Private", value: "Small Private", rm_amt: 130 },
  { label: "Regular Private", value: "Regular Private", rm_amt: 130 },
  { label: "Large Private", value: "Large Private", rm_amt: 130 },
  { label: "Suite", value: "Suite", rm_amt: 135 },
  { label: "ICU", value: "ICU", rm_amt: 135 },
];

const getRnbUnitAmount = (roomType) =>
  RNB_OPTIONS.find((option) => option.value === roomType)?.rm_amt || 0;

const calculateRnbAmount = (roomRate, days) => {
  const rate = Number(roomRate || 0);
  const totalDays = Number(days || 0);
  return rate > 0 && totalDays > 0 ? (rate * totalDays).toFixed(2) : "";
};

const calculateRuvAmount = (ruvUnit, roomType) => {
  const unit = Number(ruvUnit || 0);
  const unitAmount = getRnbUnitAmount(roomType);
  return unit > 0 && unitAmount > 0 ? (unit * unitAmount).toFixed(2) : "";
};

const resolveLoaDocumentUrl = (data = {}) => {
  const documentGeneration =
    data.loa_document_generation ||
    data.loaDocumentGeneration ||
    data.loa_pdf_generation ||
    data.loaPdfGeneration ||
    {};
  const generatedFile =
    documentGeneration.generated_file ||
    documentGeneration.generatedFile ||
    {};

  return (
    documentGeneration.signed_url ||
    documentGeneration.signedUrl ||
    documentGeneration.public_share_url ||
    documentGeneration.publicShareUrl ||
    generatedFile.public_share_url ||
    generatedFile.publicShareUrl ||
    generatedFile.signed_url ||
    generatedFile.signedUrl ||
    data.loa_document_url ||
    data.loaDocumentUrl ||
    ""
  );
};

const openLoaDocument = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const buildViewLoaDocumentAlertOptions = (url) =>
  url
    ? {
        confirmButtonText: "View LOA Document",
        showCancelButton: true,
        cancelButtonText: "Close",
        cancelButtonColor: "#6b7280",
        preConfirm: () => {
          openLoaDocument(url);
          return true;
        },
      }
    : {
        confirmButtonText: "OK",
      };

const isTruthyApprovalFlag = (value) =>
  value === true || String(value).trim().toLowerCase() === "true";

const buildApprovalMessage = (transNo) =>
  `TRN# ${transNo || "Transaction"} needs approval, please notify your TL/supervisor.`;

const getApprovalNotice = (responseData = {}, fallbackTransNo) => {
  const summary = responseData.summary || {};
  const approvalStepData = summary.step10?.data || summary.step5?.data || {};
  const status =
    responseData.approval_status ||
    responseData.approvalStatus ||
    approvalStepData.res ||
    approvalStepData["@res"] ||
    "";
  const needsApproval =
    isTruthyApprovalFlag(responseData.needs_approval) ||
    isTruthyApprovalFlag(responseData.needsApproval) ||
    String(status).trim().toLowerCase() === "needs approval";
  const resolvedTransNo =
    responseData.trans_no ||
    responseData.transNo ||
    fallbackTransNo;

  return {
    needsApproval,
    status,
    message:
      responseData.approval_message ||
      responseData.approvalMessage ||
      (needsApproval ? buildApprovalMessage(resolvedTransNo) : ""),
  };
};

const FlowType4ContinueModal = ({
  isOpen,
  onClose,
  onForceClose,
  onCancel,
  transNo,
  providerId,
  selectedCoverage,
}) => {
  const playConfirmSound = useCallback(() => {
    try {
      const audio = new Audio("/mixkit-software-interface-back-2575.wav");
      audio.volume = 0.9;
      audio.play().catch(() => {});
    } catch (error) {
      // Ignore audio playback errors (autoplay restrictions, etc.)
    }
  }, []);

  const applyAckStyles = useCallback(() => {
    const popup = Swal.getPopup();
    if (!popup) return;
    let style = popup.querySelector("#ack-checkbox-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "ack-checkbox-style";
      style.textContent = `
        .ack-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ack-checkbox input {
          transform: scale(1.2);
          margin-right: 6px;
        }
        .ack-checkbox.ack-pulse {
          background: #fff7ed;
          border-color: #fb923c;
          box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.35);
          animation: ackPulse 1s ease-in-out 2;
        }
        @keyframes ackPulse {
          0% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.55); }
          70% { box-shadow: 0 0 0 6px rgba(251, 146, 60, 0); }
          100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0); }
        }
      `;
      popup.appendChild(style);
    }
    const checkbox = popup.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.add("ack-checkbox");
    }
  }, []);

  const pulseAck = useCallback(() => {
    applyAckStyles();
    const popup = Swal.getPopup();
    const checkbox = popup?.querySelector(".swal2-checkbox");
    if (checkbox) {
      checkbox.classList.remove("ack-pulse");
      void checkbox.offsetWidth;
      checkbox.classList.add("ack-pulse");
    }
  }, [applyAckStyles]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [rvuOptions, setRvuOptions] = useState([]);
  const [cptOptions, setCptOptions] = useState([]);
  const [physicianOptions, setPhysicianOptions] = useState([]);

  const [remarks, setRemarks] = useState("");
  const [dischargeSoa, setDischargeSoa] = useState("");
  const [soaFile, setSoaFile] = useState(null);
  const [soaUploadInfo, setSoaUploadInfo] = useState(null);
  const [soaUploading, setSoaUploading] = useState(false);
  const [selectedIcd, setSelectedIcd] = useState(null);

  const [rnb, setRnb] = useState({
    room_type: "",
    room_no: "",
    no_of_days: "",
    room_rate: "",
    amount: "",
  });
  const [ruv, setRuv] = useState({
    ruv_code: "",
    remarks: "",
    rnb: "",
    ruv_unit: "",
    amount: "",
    philhealth: "",
    or_amount: "",
  });

  const [cptRows, setCptRows] = useState([]);
  const [cptPhilhealth, setCptPhilhealth] = useState("");
  const [physicianRows, setPhysicianRows] = useState([]);
  const [uncoveredAmount, setUncoveredAmount] = useState("");
  const [uncoveredRemarks, setUncoveredRemarks] = useState("");

  const formatRuvOption = useCallback((row) => {
    const rvuCode = row.rvu_code || row.code || row.value || "";
    const rvuDescription = row.rvu_description || row.description || "";
    return {
      value: rvuCode,
      label: row.label || `${rvuCode} - ${rvuDescription}`,
      rvu_code: rvuCode,
      rvu_description: rvuDescription,
      ruv_unit: row.ruv_unit || row.rvu_unit || row.value_data || row.value || "",
    };
  }, []);

  const formatCptOption = useCallback((row) => {
    const cptCode = row.cpt_code || row.value || "";
    const cptDescription = row.cpt_desc || row.description || "";
    return {
      value: cptCode,
      label: row.label || `${cptCode} - ${cptDescription}`,
      cpt_code: cptCode,
      cpt_desc: cptDescription,
      cpt_rate: row.cpt_rate ?? row.default_price ?? "",
      provider_cpt_code: row.provider_cpt_code || cptCode,
      cpt_pkg_code: row.cpt_pkg_code || "",
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setShowSummary(false);
    setSummaryData(null);
    setLoading(true);
    axios
      .get(`${BASE_URL}/generate-loa/transactions/${transNo}/opd-or/initial`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      })
      .then((res) => {
        const payload = res.data?.data || {};
        const rvuData = payload.rvu || [];
        const cptData = payload.cpts || [];
        const physicians = payload.physicians || [];

        setRvuOptions(
          rvuData.map((row) => formatRuvOption(row))
        );
        setCptOptions(
          cptData.map((row) => formatCptOption(row))
        );
        setPhysicianOptions(
          physicians.map((row) => ({
            value: row.physician_id || row.tbl_id,
            label:
              row.full_name ||
              row.physician_name ||
              row.first_name + " " + row.last_name,
            physician_id: row.physician_id || row.tbl_id,
            physician_name:
              row.full_name ||
              row.physician_name ||
              row.first_name + " " + row.last_name,
          }))
        );
      })
      .catch(() => {
        toast.error("Failed to load OPD-OR data.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [formatCptOption, formatRuvOption, isOpen, transNo]);

  useEffect(() => {
    if (!isOpen) {
      setShowSummary(false);
      setSummaryData(null);
    }
  }, [isOpen]);

  const loadIcdOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}/generate-loa/icds`, {
        params: { q: inputValue },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      if (response.data.status === "success") {
        callback(response.data.data);
      } else {
        callback([]);
      }
    } catch (error) {
      callback([]);
    }
  };

  const loadRuvOptions = useCallback(
    async (inputValue, callback) => {
      if (inputValue && inputValue.length < 2) {
        callback([]);
        return;
      }
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/transactions/${transNo}/opd-or/ruvs`,
          {
            params: { q: inputValue || "", limit: 20 },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data?.success) {
          callback((response.data.data || []).map((row) => formatRuvOption(row)));
        } else {
          callback([]);
        }
      } catch (error) {
        callback([]);
      }
    },
    [formatRuvOption, transNo]
  );

  const loadCptOptions = useCallback(
    async (inputValue, callback) => {
      if (inputValue && inputValue.length < 2) {
        callback([]);
        return;
      }
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/transactions/${transNo}/opd-or/cpts`,
          {
            params: { q: inputValue || "", limit: 20 },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data?.success) {
          callback((response.data.data || []).map((row) => formatCptOption(row)));
        } else {
          callback([]);
        }
      } catch (error) {
        callback([]);
      }
    },
    [formatCptOption, transNo]
  );

  const handleRoomTypeChange = (roomType) => {
    setRnb((prev) => ({
      ...prev,
      room_type: roomType,
    }));
    setRuv((prev) => ({
      ...prev,
      rnb: roomType,
      amount: calculateRuvAmount(prev.ruv_unit, roomType),
    }));
  };

  const updateRnbNumber = (field, value) => {
    setRnb((prev) => {
      const next = { ...prev, [field]: value };
      return {
        ...next,
        amount: calculateRnbAmount(next.room_rate, next.no_of_days),
      };
    });
  };

  const handleRuvSelect = (opt) => {
    const ruvUnit = opt?.ruv_unit || "";
    if (opt) {
      setRvuOptions((prev) =>
        prev.some((item) => item.value === opt.value) ? prev : [...prev, opt]
      );
    }
    setRuv((prev) => ({
      ...prev,
      ruv_code: opt?.value || "",
      ruv_unit: ruvUnit,
      amount: calculateRuvAmount(ruvUnit, prev.rnb || rnb.room_type),
    }));
  };

  const handleRuvUnitChange = (value) => {
    setRuv((prev) => ({
      ...prev,
      ruv_unit: value,
      amount: calculateRuvAmount(value, prev.rnb || rnb.room_type),
    }));
  };

  const handleCptSelect = (index, opt) => {
    setCptRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              cpt: opt,
              provider_cpt_code:
                opt?.provider_cpt_code || opt?.cpt_code || opt?.value || "",
              cpt_rate: opt?.cpt_rate ?? "",
              cpt_pkg_code: opt?.cpt_pkg_code || "",
            }
          : row
      )
    );
  };

  const updateCptRow = (index, field, value) => {
    setCptRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const totalInc = useMemo(() => {
    const rnbAmt = parseFloat(rnb.amount || 0);
    const ruvAmt = parseFloat(ruv.amount || 0);
    const cptAmt = cptRows.reduce(
      (sum, row) => sum + parseFloat(row.cpt_rate || 0),
      0
    );
    const physAmt = physicianRows.reduce(
      (sum, row) => sum + parseFloat(row.amount || 0),
      0
    );
    return rnbAmt + ruvAmt + cptAmt + physAmt;
  }, [rnb.amount, ruv.amount, cptRows, physicianRows]);

  const totalPh = useMemo(() => {
    const ruvPh = parseFloat(ruv.philhealth || 0);
    const cptPh = parseFloat(cptPhilhealth || 0);
    const physPh = physicianRows.reduce(
      (sum, row) => sum + parseFloat(row.philhealth || 0),
      0
    );
    return ruvPh + cptPh + physPh;
  }, [ruv.philhealth, cptPhilhealth, physicianRows]);

  const totalCovered = useMemo(() => {
    const uncovered = parseFloat(uncoveredAmount || 0);
    const covered = totalInc - totalPh - uncovered;
    return covered < 0 ? 0 : covered;
  }, [totalInc, totalPh, uncoveredAmount]);

  const selectedIcdLabel =
    selectedIcd?.label || selectedIcd?.text || selectedIcd?.value || "";
  const selectedRuvLabel =
    rvuOptions.find((opt) => opt.value === ruv.ruv_code)?.label ||
    ruv.ruv_code ||
    "";

  const toNumberOrFallback = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const formatMoney = (value) => {
    const num = toNumberOrFallback(value, 0);
    return `PHP ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const collectToastMessages = (value) => {
    if (!value) return [];
    if (typeof value === "string" || typeof value === "number") {
      return [String(value)];
    }
    if (Array.isArray(value)) {
      return value.flatMap((item) => collectToastMessages(item));
    }
    if (typeof value === "object") {
      return Object.values(value).flatMap((item) => collectToastMessages(item));
    }
    return [];
  };

  const getApiToastMessage = (data, fallback) => {
    const messages = [
      ...collectToastMessages(data?.message),
      ...collectToastMessages(data?.errors),
      ...collectToastMessages(typeof data === "string" ? data : null),
    ]
      .map((message) => message.trim())
      .filter(Boolean);
    const uniqueMessages = [...new Set(messages)];

    if (uniqueMessages.length === 0) {
      return fallback;
    }
    if (uniqueMessages.length === 1) {
      return uniqueMessages[0];
    }

    return (
      <div className="space-y-1">
        {uniqueMessages.map((message, index) => (
          <div key={`api-toast-message-${index}`}>{message}</div>
        ))}
      </div>
    );
  };

  const showApiErrorToast = (data, fallback) => {
    toast.error(getApiToastMessage(data, fallback));
  };

  const opdOrBenefitLimit = summaryData?.opd_or_plan_benefit_limit || null;
  const summaryTotals =
    summaryData?.totals || opdOrBenefitLimit?.totals || {};
  const displayedTotalInc = toNumberOrFallback(
    summaryTotals.total_inc,
    totalInc
  );
  const displayedTotalPh = toNumberOrFallback(summaryTotals.total_ph, totalPh);
  const displayedTotalCovered = toNumberOrFallback(
    summaryTotals.total_covered,
    totalCovered
  );
  const displayedUncoveredAmount = toNumberOrFallback(
    summaryTotals.uncovered_amount,
    Number(uncoveredAmount || 0)
  );
  const displayedAutoExcess = toNumberOrFallback(
    opdOrBenefitLimit?.auto_excess_amount,
    0
  );
  const opdOrBenefitLimitMode =
    opdOrBenefitLimit?.mode === "as_charged"
      ? "As Charged"
      : opdOrBenefitLimit?.mode === "fixed_amount"
      ? "Fixed Amount"
      : opdOrBenefitLimit?.mode || "N/A";

  const addCptRow = () => {
    setCptRows((prev) => [
      ...prev,
      { cpt: null, provider_cpt_code: "", cpt_rate: "" },
    ]);
  };

  const removeCptRow = (index) => {
    setCptRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSoaFileChange = async (event) => {
    const file = event.target.files?.[0] || null;
    setSoaFile(file);
    setSoaUploadInfo(null);

    if (!file) {
      setDischargeSoa("");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("trans_no", transNo);
    setSoaUploading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/generate-loa/opd-or/upload-soa`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.status === "success") {
        const uploadInfo = {
          upload_id: response.data.upload_id,
          signed_url: response.data.signed_url,
          file_name: response.data.file_name || file.name,
        };
        setSoaUploadInfo(uploadInfo);
        setDischargeSoa(uploadInfo.file_name);
        toast.success(`SOA file uploaded: ${file.name}`);
      } else {
        setDischargeSoa("");
        showApiErrorToast(response.data, "Failed to upload SOA file.");
      }
    } catch (error) {
      setDischargeSoa("");
      showApiErrorToast(error.response?.data, "Failed to upload SOA file.");
    } finally {
      setSoaUploading(false);
    }
  };

  const addPhysicianRow = () => {
    setPhysicianRows((prev) => [
      ...prev,
      { physician: null, amount: "", philhealth: "" },
    ]);
  };

  const updatePhysicianRow = (index, field, value) => {
    setPhysicianRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removePhysicianRow = (index) => {
    setPhysicianRows((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = () => ({
    remarks,
    chief_complaint: "",
    icd_code: selectedIcd?.id || selectedIcd?.value || "",
    rnb: {
      room_type: rnb.room_type,
      room_no: rnb.room_no,
      no_of_days: rnb.no_of_days,
      room_rate: rnb.room_rate,
    },
    ruv: {
      ruv_code: ruv.ruv_code,
      remarks: ruv.remarks,
      rnb: ruv.rnb,
      ruv_unit: ruv.ruv_unit,
      amount: ruv.amount,
      philhealth: ruv.philhealth,
      or_amount: ruv.or_amount,
    },
    cpt_rows: cptRows
      .filter((row) => row.cpt?.cpt_code || row.cpt?.value)
      .map((row) => ({
        cpt_code: row.cpt?.cpt_code || row.cpt?.value,
        provider_cpt_code: row.provider_cpt_code || row.cpt?.cpt_code,
        cpt_rate: row.cpt_rate,
      })),
    physician_rows: physicianRows.map((row) => ({
      physician_id: row.physician?.physician_id || row.physician?.value || "",
      physician_name:
        row.physician?.physician_name || row.physician?.label || "",
      amount: row.amount,
      philhealth: row.philhealth,
    })),
    totals: {
      total_inc: totalInc,
      total_ph: totalPh,
      total_covered: totalCovered,
      uncovered_amount: uncoveredAmount || 0,
      uncovered_remarks: uncoveredRemarks || "",
    },
    discharge_soa: dischargeSoa || "",
  });

  const validateInputs = () => {
    if (!selectedIcd) {
      toast.error("Please select an ICD code.");
      return false;
    }
    if (physicianRows.length === 0) {
      toast.error("Please add at least one physician.");
      return false;
    }
    if (soaUploading) {
      toast.error("Please wait for the SOA upload to finish.");
      return false;
    }
    return true;
  };

  const handlePreviewSummary = async () => {
    if (!validateInputs()) {
      return;
    }
    setSummaryLoading(true);
    try {
      const payload = buildPayload();
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions/${transNo}/loa-type4?summary=1`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      if (response.data?.success) {
        setSummaryData(response.data?.data || {});
        setShowSummary(true);
      } else {
        showApiErrorToast(response.data, "Failed to generate summary.");
      }
    } catch (error) {
      showApiErrorToast(error.response?.data, "Failed to generate summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions/${transNo}/loa-type4`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      if (response.data?.success) {
        const responseData = response.data?.data || {};
        const approvalNotice = getApprovalNotice(responseData, transNo);
        const loaDocumentUrl = approvalNotice.needsApproval
          ? ""
          : resolveLoaDocumentUrl(responseData);
        const alertText = approvalNotice.needsApproval
          ? approvalNotice.message || buildApprovalMessage(transNo)
          : response.data?.message || "OPD-OR LOA generated successfully.";
        await Swal.fire({
          icon: approvalNotice.needsApproval ? "info" : "success",
          title: approvalNotice.needsApproval ? "TRN Needs Approval" : "LOA Generated!",
          text: alertText,
          confirmButtonColor: approvalNotice.needsApproval ? "#3b82f6" : "#10b981",
          allowOutsideClick: false,
          ...(approvalNotice.needsApproval
            ? { confirmButtonText: "OK" }
            : buildViewLoaDocumentAlertOptions(loaDocumentUrl)),
        });
        if (approvalNotice.needsApproval) {
          toast.info(alertText);
        } else {
          toast.success("OPD-OR LOA generated successfully.");
        }
        setShowSummary(false);
        setSummaryData(null);
        if (onForceClose) {
          onForceClose();
        } else {
          onClose();
        }
      } else {
        showApiErrorToast(response.data, "Failed to generate OPD-OR LOA.");
      }
    } catch (error) {
      showApiErrorToast(error.response?.data, "Failed to generate OPD-OR LOA.");
    } finally {
      setSaving(false);
    }
  };

  const handleProceedGenerate = async () => {
    if (!validateInputs()) {
      return;
    }

    const step1 = await Swal.fire({
      title: "Are you sure the details are correct?",
      text: "Please review the summary before proceeding.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Review again",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#e5e7eb",
      allowOutsideClick: false,
      didOpen: () => {
        playConfirmSound();
      },
    });

    if (!step1.isConfirmed) {
      return;
    }

    const step2 = await Swal.fire({
      title: "Acknowledgement",
      input: "checkbox",
      inputValue: 0,
      inputPlaceholder:
        "I acknowledge the warning and confirm the details above are accurate. I will proceed to generate the LOA.",
      confirmButtonText: "Proceed Generate LOA",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#e5e7eb",
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (value) return undefined;
        pulseAck();
        return `Please acknowledge before proceeding.<div style="font-size:12px;color:#6b7280;margin-top:4px;">You need to click the checkbox on "I acknowledge the warning..." message before proceeding.</div>`;
      },
      didOpen: () => {
        playConfirmSound();
        applyAckStyles();
      },
    });

    if (!step2.isConfirmed) {
      return;
    }

    await handleSubmit();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          className={`bg-white rounded-2xl p-5 w-full mx-4 overflow-y-auto shadow-2xl border border-gray-100 ${
            isMaximized
              ? "max-w-[98vw] max-h-[96vh]"
              : "max-w-7xl max-h-[94vh]"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon icon={faFileMedical} className="text-blue-600" />
              {showSummary ? "Summary Review:" : "Process OPD-OR -"}{" "}
              {selectedCoverage?.text || "OUTPATIENT-OPD-OR"}
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMaximized((prev) => !prev)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title={isMaximized ? "Restore modal size" : "Maximize modal"}
              >
                <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-600">
              <FontAwesomeIcon icon={faSpinner} spin />
              Loading OPD-OR data...
            </div>
          ) : showSummary ? (
            <div className="space-y-4 text-sm">
              <section className="bg-gray-50 p-3 rounded-xl">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Summary Preview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <div>Member: {summaryData?.member_name || "N/A"}</div>
                  <div>Member ID: {summaryData?.member_id || "N/A"}</div>
                  <div>Transaction #: {summaryData?.trans_no || transNo}</div>
                  <div>Coverage: {selectedCoverage?.text || "OUTPATIENT-OPD-OR"}</div>
                </div>
              </section>

              <section className="bg-white border rounded-xl p-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Total Incurred</p>
                    <p className="font-semibold">{formatMoney(displayedTotalInc)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Philhealth</p>
                    <p className="font-semibold">{formatMoney(displayedTotalPh)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Covered</p>
                    <p className="font-semibold">{formatMoney(displayedTotalCovered)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Excess / Member</p>
                    <p className="font-semibold">{formatMoney(displayedUncoveredAmount)}</p>
                  </div>
                </div>
              </section>

              {opdOrBenefitLimit && (
                <section className="bg-gray-50 p-3 rounded-xl">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    OPD-OR Benefit Limit
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-700">
                    <div>Mode: {opdOrBenefitLimitMode}</div>
                    <div>OPD-OR Basic: {opdOrBenefitLimit.opd_or_basic_limit || "N/A"}</div>
                    <div>
                      Inpatient Max:{" "}
                      {opdOrBenefitLimit.inpatient_max_limit_amount !== null &&
                      opdOrBenefitLimit.inpatient_max_limit_amount !== undefined
                        ? formatMoney(opdOrBenefitLimit.inpatient_max_limit_amount)
                        : opdOrBenefitLimit.inpatient_max_basic_limit || "N/A"}
                    </div>
                    <div>Chargeable: {formatMoney(opdOrBenefitLimit.chargeable_amount)}</div>
                    <div>Company Covered: {formatMoney(opdOrBenefitLimit.company_covered_amount)}</div>
                    <div>Auto Excess: {formatMoney(displayedAutoExcess)}</div>
                  </div>
                </section>
              )}

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">Diagnosis</h4>
                <div className="text-gray-700">
                  {selectedIcdLabel || "N/A"}
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">Room & Board</h4>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-gray-700">
                  <div>Type: {rnb.room_type || "N/A"}</div>
                  <div>Room No: {rnb.room_no || "N/A"}</div>
                  <div>Days: {rnb.no_of_days || "0"}</div>
                  <div>Rate: {formatMoney(rnb.room_rate)}</div>
                  <div>Amount: {formatMoney(rnb.amount)}</div>
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">RUV</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-700">
                  <div>Code: {selectedRuvLabel || "N/A"}</div>
                  <div>RNB: {ruv.rnb || "N/A"}</div>
                  <div>Unit: {ruv.ruv_unit || "0"}</div>
                  <div>Amount: {formatMoney(ruv.amount)}</div>
                  <div>Philhealth: {formatMoney(ruv.philhealth)}</div>
                  <div>OR Amount: {formatMoney(ruv.or_amount)}</div>
                  <div className="md:col-span-3">Remarks: {ruv.remarks || "N/A"}</div>
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">CPT</h4>
                {cptRows.length === 0 ? (
                  <div className="text-gray-600">No CPT rows.</div>
                ) : (
                  <div className="space-y-2">
                    {cptRows.map((row, idx) => (
                      <div key={`sum-cpt-${idx}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                        <div>{row.cpt?.label || row.cpt?.cpt_code || row.cpt?.value || "N/A"}</div>
                        <div>Amount: {formatMoney(row.cpt_rate)}</div>
                      </div>
                    ))}
                    <div className="text-gray-700">
                      CPT Philhealth Total: {formatMoney(cptPhilhealth)}
                    </div>
                  </div>
                )}
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">Physicians</h4>
                {physicianRows.length === 0 ? (
                  <div className="text-gray-600">No physician rows.</div>
                ) : (
                  <div className="space-y-2">
                    {physicianRows.map((row, idx) => (
                      <div key={`sum-phys-${idx}`} className="grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-700">
                        <div>{row.physician?.label || row.physician?.physician_name || "N/A"}</div>
                        <div>Amount: {formatMoney(row.amount)}</div>
                        <div>Philhealth: {formatMoney(row.philhealth)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">Notes</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <div>Uncovered Amount: {formatMoney(displayedUncoveredAmount)}</div>
                  <div>Uncovered Remarks: {uncoveredRemarks || "N/A"}</div>
                  <div>SOA Reference: {dischargeSoa || "N/A"}</div>
                  <div>Remarks: {remarks || "N/A"}</div>
                </div>
              </section>

              {summaryData?.summary && (
                <section className="bg-gray-50 p-3 rounded-xl">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Step Results
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-700">
                    {Object.entries(summaryData.summary).map(
                      ([key, value]) => (
                        <div key={key} className="flex justify-between gap-2">
                          <span className="font-medium">{key}</span>
                          <span>
                            {value?.message ||
                              (value?.success ? "OK" : "Failed")}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </section>
              )}

              {summaryData?.summary?.step10?.data && (
                <section className="bg-gray-50 p-3 rounded-xl">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Approval Check
                  </h4>
                  <div className="text-sm text-gray-700">
                    <div>
                      Result: {summaryData.summary.step10.data.res || "N/A"}
                    </div>
                    <div>
                      Approval Type:{" "}
                      {summaryData.summary.step10.data.approval_type || "N/A"}
                    </div>
                  </div>
                </section>
              )}

            </div>
          ) : (
            <div className="space-y-6">
              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faStethoscope} className="text-indigo-600" />
                  <h3 className="font-semibold text-gray-800">Diagnosis (ICD)</h3>
                </div>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadIcdOptions}
                  value={selectedIcd}
                  onChange={setSelectedIcd}
                  placeholder="Type to search ICD (min 2 chars)..."
                  styles={selectStyles}
                />
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faBed} className="text-emerald-600" />
                  <h3 className="font-semibold text-gray-800">Room and Board</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <select
                    value={rnb.room_type}
                    onChange={(e) => handleRoomTypeChange(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Select Room Type</option>
                    {RNB_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Room No"
                    value={rnb.room_no}
                    onChange={(e) =>
                      setRnb((prev) => ({ ...prev, room_no: e.target.value }))
                    }
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Days"
                    value={rnb.no_of_days}
                    onChange={(e) => updateRnbNumber("no_of_days", e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Room Rate"
                    value={rnb.room_rate}
                    onChange={(e) => updateRnbNumber("room_rate", e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Amount"
                    value={rnb.amount}
                    readOnly
                    className="border rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-600"
                  />
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faSyringe} className="text-rose-600" />
                  <h3 className="font-semibold text-gray-800">RUV Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={rvuOptions}
                    loadOptions={loadRuvOptions}
                    value={
                      rvuOptions.find((opt) => opt.value === ruv.ruv_code) ||
                      null
                    }
                    onChange={handleRuvSelect}
                    placeholder="Select RUV"
                    styles={selectStyles}
                  />
                  <input
                    type="text"
                    placeholder="RUV Remarks"
                    value={ruv.remarks}
                    onChange={(e) =>
                      setRuv((prev) => ({ ...prev, remarks: e.target.value }))
                    }
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="R&B Type"
                    value={ruv.rnb}
                    readOnly
                    className="border rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-600"
                  />
                  <input
                    type="number"
                    placeholder="RUV Unit"
                    value={ruv.ruv_unit}
                    onChange={(e) => handleRuvUnitChange(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="RUV Amount"
                    value={ruv.amount}
                    onChange={(e) =>
                      setRuv((prev) => ({ ...prev, amount: e.target.value }))
                    }
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="RUV Philhealth"
                    value={ruv.philhealth}
                    onChange={(e) =>
                      setRuv((prev) => ({ ...prev, philhealth: e.target.value }))
                    }
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="OR Amount"
                    value={ruv.or_amount}
                    readOnly
                    className="border rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-600"
                  />
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faFileMedical} className="text-purple-600" />
                    <h3 className="font-semibold text-gray-800">CPT Procedures</h3>
                  </div>
                  <button
                    onClick={addCptRow}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add CPT
                  </button>
                </div>
                <div className="space-y-2">
                  {cptRows.map((row, index) => (
                    <div
                      key={`cpt-${index}`}
                      className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center"
                    >
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={cptOptions}
                        loadOptions={loadCptOptions}
                        value={row.cpt}
                        onChange={(opt) => handleCptSelect(index, opt)}
                        placeholder="Select CPT"
                        styles={selectStyles}
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        value={row.cpt_rate}
                        onChange={(e) =>
                          updateCptRow(index, "cpt_rate", e.target.value)
                        }
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => removeCptRow(index)}
                        className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Remove
                      </button>
                    </div>
                  ))}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                    <input
                      type="number"
                      placeholder="CPT Philhealth Total"
                      value={cptPhilhealth}
                      onChange={(e) => setCptPhilhealth(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUserDoctor} className="text-teal-600" />
                    <h3 className="font-semibold text-gray-800">Physicians</h3>
                  </div>
                  <button
                    onClick={addPhysicianRow}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Physician
                  </button>
                </div>
                <div className="space-y-2">
                  {physicianRows.map((row, index) => (
                    <div
                      key={`phys-${index}`}
                      className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center"
                    >
                      <Select
                        value={row.physician}
                        onChange={(opt) =>
                          updatePhysicianRow(index, "physician", opt)
                        }
                        options={physicianOptions}
                        placeholder="Select Physician"
                        styles={selectStyles}
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        value={row.amount}
                        onChange={(e) =>
                          updatePhysicianRow(index, "amount", e.target.value)
                        }
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Philhealth"
                        value={row.philhealth}
                        onChange={(e) =>
                          updatePhysicianRow(index, "philhealth", e.target.value)
                        }
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => removePhysicianRow(index)}
                        className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="number"
                    placeholder="Uncovered Amount"
                    value={uncoveredAmount}
                    onChange={(e) => setUncoveredAmount(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Uncovered Remarks"
                    value={uncoveredRemarks}
                    onChange={(e) => setUncoveredRemarks(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                  <label className="border rounded-md px-3 py-2 text-sm bg-white flex items-center gap-2 cursor-pointer">
                    <FontAwesomeIcon icon={faUpload} className="text-blue-600" />
                    <span className="truncate">
                      {soaUploading
                        ? "Uploading SOA..."
                        : soaFile?.name || "SOA File/Reference"}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSoaFileChange}
                      className="hidden"
                      disabled={soaUploading}
                    />
                  </label>
                </div>
                {(soaUploadInfo || dischargeSoa) && (
                  <p className="mt-2 text-xs text-gray-600">
                    Saved SOA Reference: {soaUploadInfo?.file_name || dischargeSoa}
                  </p>
                )}
              </section>

              <section className="bg-white border rounded-xl p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Total Incurred</p>
                    <p className="font-semibold">PHP {totalInc.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Philhealth</p>
                    <p className="font-semibold">PHP {totalPh.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Covered</p>
                    <p className="font-semibold">PHP {totalCovered.toFixed(2)}</p>
                  </div>
                </div>
              </section>

              <section className="bg-gray-50 p-3 rounded-xl">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </section>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                disabled={saving || summaryLoading}
              >
                Cancel Transaction
              </button>
            )}
            <button
              onClick={() => {
                if (showSummary) {
                  setShowSummary(false);
                  return;
                }
                onClose();
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              disabled={saving || summaryLoading}
            >
              {showSummary ? "Back" : "Close"}
            </button>
            <button
              onClick={showSummary ? handleProceedGenerate : handlePreviewSummary}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              disabled={saving || summaryLoading}
            >
              {(saving || summaryLoading) && (
                <FontAwesomeIcon icon={faSpinner} spin />
              )}
              {showSummary ? "Proceed Generate LOA" : "Proceed to Summary"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FlowType4ContinueModal;

